import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let loading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile didn't load")));
    s.onerror = () => {
      loading = null;
      reject(new Error("Couldn't load the captcha. Check your connection or ad blocker."));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export interface TurnstileHandle {
  /** Tokens work once, so reset after every submit. */
  reset: () => void;
}

/** Cloudflare Turnstile widget. `onToken(null)` when the token expires or the check fails. */
export const Turnstile = forwardRef<TurnstileHandle, { siteKey: string; onToken: (token: string | null) => void; onError?: (message: string) => void }>(
  function Turnstile({ siteKey, onToken, onError }, ref) {
    const el = useRef<HTMLDivElement>(null);
    const id = useRef<string | null>(null);
    const handlers = useRef({ onToken, onError });
    handlers.current = { onToken, onError };

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (id.current && window.turnstile) window.turnstile.reset(id.current);
        handlers.current.onToken(null);
      },
    }));

    useEffect(() => {
      let cancelled = false;
      loadTurnstile()
        .then((t) => {
          if (cancelled || !el.current) return;
          id.current = t.render(el.current, {
            sitekey: siteKey,
            callback: (token: string) => handlers.current.onToken(token),
            "expired-callback": () => handlers.current.onToken(null),
            "error-callback": () => handlers.current.onToken(null),
          });
        })
        .catch((e: Error) => handlers.current.onError?.(e.message));
      return () => {
        cancelled = true;
        if (id.current && window.turnstile) window.turnstile.remove(id.current);
        id.current = null;
      };
    }, [siteKey]);

    return <div ref={el} style={{ minHeight: 65 }} />;
  },
);
